// Card "Diagnostica connessione" della scheda chiamata super-admin (TODO #9):
// due colonne, chiamante e consulente, con il campione getStats() inviato da
// ciascun lato + giudizio sintetico Buona / Discreta / Scarsa.
import type { CallNetDiagnostics } from '@klaro7/shared-types';

import { netQuality, type NetQuality } from '@/features/calls/lib/netDiagnostics';

const qualityBadge: Record<NetQuality, { label: string; className: string }> = {
  good: { label: 'Buona', className: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  fair: { label: 'Discreta', className: 'bg-amber-50 text-amber-700 ring-amber-200' },
  poor: { label: 'Scarsa', className: 'bg-red-50 text-red-700 ring-red-200' },
};

const candidateLabels: Record<string, string> = {
  host: 'diretta (rete locale)',
  srflx: 'diretta (NAT)',
  prflx: 'diretta (NAT)',
  relay: 'relay TURN',
};

const ms = (v?: number) => (v == null ? '—' : `${Math.round(v)} ms`);

function networkLabel(d: CallNetDiagnostics): string {
  const parts: string[] = [];
  if (d.networkType) parts.push(d.networkType === 'cellular' ? 'rete mobile' : d.networkType);
  if (d.effectiveType) parts.push(d.effectiveType.toUpperCase());
  if (d.downlinkMbps != null) parts.push(`~${d.downlinkMbps} Mbps`);
  return parts.length ? parts.join(' · ') : '—';
}

function pathLabel(d: CallNetDiagnostics): string {
  if (d.relay == null && !d.localCandidateType) return '—';
  const kind = d.relay ? 'relay TURN' : candidateLabels[d.localCandidateType ?? ''] ?? 'diretta';
  return d.protocol ? `${kind} · ${d.protocol.toUpperCase()}` : kind;
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <dt className="text-sm text-neutral-500">{label}</dt>
      <dd className="text-right text-sm font-medium text-neutral-900">{value}</dd>
    </div>
  );
}

function SideColumn({ title, diag }: { title: string; diag: CallNetDiagnostics | null }) {
  const quality = netQuality(diag);
  return (
    <div className="min-w-0">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-neutral-900">{title}</h3>
        {quality && (
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${qualityBadge[quality].className}`}
          >
            {qualityBadge[quality].label}
          </span>
        )}
      </div>
      {!diag ? (
        <p className="mt-2 text-sm text-neutral-500">Non disponibile</p>
      ) : (
        <dl className="mt-2 divide-y divide-neutral-100">
          {diag.phase === 'failed' && (
            <p className="py-1.5 text-sm font-medium text-red-600">
              Connessione non riuscita (ICE fallito)
            </p>
          )}
          <Row label="Latenza ping (segnalazione)" value={ms(diag.pingMs)} />
          <Row label="RTT media" value={ms(diag.rttMs)} />
          <Row label="Jitter" value={ms(diag.jitterMs)} />
          <Row
            label="Pacchetti persi"
            value={
              diag.packetLossPct == null
                ? '—'
                : `${diag.packetLossPct.toLocaleString('it-IT')}%${
                    diag.packetsLost != null ? ` (${diag.packetsLost})` : ''
                  }`
            }
          />
          <Row
            label="Banda stimata in uscita"
            value={diag.outgoingBitrateKbps == null ? '—' : `${Math.round(diag.outgoingBitrateKbps)} kbps`}
          />
          <Row label="Percorso media" value={pathLabel(diag)} />
          <Row label="Rete" value={networkLabel(diag)} />
          <Row label="Browser" value={diag.browser ?? '—'} />
          <Row
            label="Sistema / dispositivo"
            value={[diag.os, diag.deviceModel].filter(Boolean).join(' · ') || '—'}
          />
          {diag.audioCodec && <Row label="Codec audio" value={diag.audioCodec} />}
          {diag.sampledAt && (
            <Row
              label="Rilevato alle"
              value={new Date(diag.sampledAt).toLocaleTimeString('it-IT', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              })}
            />
          )}
        </dl>
      )}
    </div>
  );
}

export function CallNetDiagnosticsCard({
  caller,
  professional,
}: {
  caller: CallNetDiagnostics | null;
  professional: CallNetDiagnostics | null;
}) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-5">
      <h2 className="text-base font-semibold text-neutral-900">Diagnostica connessione</h2>
      <p className="mt-1 text-xs text-neutral-500">
        Rilevata da ciascun dispositivo pochi secondi dopo l&apos;avvio. Qualità: Buona = RTT &lt; 150 ms,
        perdita &lt; 1%, jitter &lt; 30 ms; Discreta = RTT &lt; 300 ms, perdita &lt; 5%; altrimenti Scarsa.
      </p>
      <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-2">
        <SideColumn title="Chiamante" diag={caller} />
        <SideColumn title="Consulente" diag={professional} />
      </div>
    </div>
  );
}
